import { environment } from '../config';

export const IMPERSONATION_EMAIL_QUERY_PARAM = 'email';
const IMPERSONATION_EMAIL_STORAGE_KEY = 'impersonation_email';

export const getImpersonationEmail = (search: string = window.location.search): string | null => {
  if (environment.isProduction) {
    return null;
  }

  const email = getRawQueryParam(search, IMPERSONATION_EMAIL_QUERY_PARAM)?.trim();
  return email || null;
};

// URLSearchParams decodes '+' as a space (form encoding), which corrupts emails such as
// "user+tag@example.com" when the address is pasted unencoded into the URL. Decode with
// decodeURIComponent instead so a literal '+' is preserved while '%2B'/'%40' still decode.
const getRawQueryParam = (search: string, name: string): string | null => {
  const query = search.startsWith('?') ? search.slice(1) : search;
  for (const pair of query.split('&')) {
    const separatorIndex = pair.indexOf('=');
    const key = separatorIndex === -1 ? pair : pair.slice(0, separatorIndex);
    if (safeDecode(key) !== name) {
      continue;
    }
    return separatorIndex === -1 ? '' : safeDecode(pair.slice(separatorIndex + 1));
  }
  return null;
};

const safeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const storeImpersonationEmail = (email: string): void => {
  sessionStorage.setItem(IMPERSONATION_EMAIL_STORAGE_KEY, email);
};

export const getStoredImpersonationEmail = (): string | null => {
  return sessionStorage.getItem(IMPERSONATION_EMAIL_STORAGE_KEY);
};

export const clearStoredImpersonationEmail = (): void => {
  sessionStorage.removeItem(IMPERSONATION_EMAIL_STORAGE_KEY);
};

export const consumeStoredImpersonationEmail = (): string | null => {
  const email = getStoredImpersonationEmail();
  clearStoredImpersonationEmail();
  return email;
};

/**
 * Carries the active impersonation email from `search` onto an in-app path so that
 * impersonation survives client-side navigation. Any `email` parameter already on the
 * path is replaced, and other parameters and any `#hash` are kept. Returns the path
 * unchanged when no one is being impersonated.
 */
export const withImpersonation = (path: string, search: string = window.location.search): string => {
  const email = getImpersonationEmail(search);
  if (!email) {
    return path;
  }

  const hashIndex = path.indexOf('#');
  const hash = hashIndex === -1 ? '' : path.slice(hashIndex);
  const pathWithQuery = hashIndex === -1 ? path : path.slice(0, hashIndex);
  const queryIndex = pathWithQuery.indexOf('?');
  const basePath = queryIndex === -1 ? pathWithQuery : pathWithQuery.slice(0, queryIndex);
  const query = queryIndex === -1 ? '' : pathWithQuery.slice(queryIndex + 1);

  // Pairs are filtered without re-encoding so the remaining parameters stay byte-for-byte intact.
  const pairs = query
    .split('&')
    .filter((pair) => pair && safeDecode(pair.split('=', 1)[0]) !== IMPERSONATION_EMAIL_QUERY_PARAM);
  pairs.push(`${IMPERSONATION_EMAIL_QUERY_PARAM}=${encodeURIComponent(email)}`);

  return `${basePath}?${pairs.join('&')}${hash}`;
};

export const getImpersonationUrl = (email: string): string => {
  const query = new URLSearchParams({
    [IMPERSONATION_EMAIL_QUERY_PARAM]: email,
  });
  return `/?${query.toString()}`;
};
